import React, { useState, useMemo } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const DeadlineCalendar = ({ tasks, onSelectDate, onUpdateTask, onCreateTask }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);

  const getDaysInMonth = (date) => {
    const year = date?.getFullYear();
    const month = date?.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay?.getDate();
    const startingDayOfWeek = firstDay?.getDay();

    return { daysInMonth, startingDayOfWeek, year, month };
  };

  const { daysInMonth, startingDayOfWeek, year, month } = getDaysInMonth(currentDate);

  const getTasksForDate = (day) => {
    const dateStr = new Date(year, month, day)?.toISOString()?.split('T')?.[0];
    return tasks?.filter(task => {
      if (!task?.due_date) return false;
      const taskDate = new Date(task?.due_date)?.toISOString()?.split('T')?.[0];
      return taskDate === dateStr;
    });
  };

  const selectedDateTasks = useMemo(() => {
    if (!selectedDate) return [];
    return getTasksForDate(selectedDate);
  }, [selectedDate, tasks]);

  const handlePreviousMonth = () => {
    setCurrentDate(new Date(year, month - 1, 1));
  };

  const handleNextMonth = () => {
    setCurrentDate(new Date(year, month + 1, 1));
  };

  const handleDateClick = (day) => {
    setSelectedDate(day);
    onSelectDate?.(new Date(year, month, day));
  };

  const isToday = (day) => {
    const today = new Date();
    return (
      day === today?.getDate() &&
      month === today?.getMonth() &&
      year === today?.getFullYear()
    );
  };

  const isOverdue = (day) => {
    const date = new Date(year, month, day);
    const today = new Date();
    today?.setHours(0, 0, 0, 0);
    return date < today;
  };

  const getPriorityColor = (priority) => {
    const colors = {
      urgent: 'bg-error',
      high: 'bg-warning',
      normal: 'bg-primary',
      low: 'bg-muted-foreground'
    };
    return colors?.[priority] || colors?.normal;
  };

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  return (
    <div className="grid lg:grid-cols-3 gap-6">
      {/* Calendar */}
      <div className="lg:col-span-2 bg-card rounded-lg border border-border p-6">
        {/* Calendar Header */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-foreground">
            {monthNames?.[month]} {year}
          </h2>
          <div className="flex gap-2">
            <button
              onClick={handlePreviousMonth}
              className="p-2 rounded-lg hover:bg-muted transition-colors"
            >
              <Icon name="ChevronLeft" size={20} color="var(--color-foreground)" />
            </button>
            <button
              onClick={() => setCurrentDate(new Date())}
              className="px-3 py-2 rounded-lg hover:bg-muted transition-colors text-sm font-medium text-foreground"
            >
              Today
            </button>
            <button
              onClick={handleNextMonth}
              className="p-2 rounded-lg hover:bg-muted transition-colors"
            >
              <Icon name="ChevronRight" size={20} color="var(--color-foreground)" />
            </button>
          </div>
        </div>

        {/* Day Names */}
        <div className="grid grid-cols-7 gap-2 mb-2">
          {dayNames?.map((day) => (
            <div key={day} className="text-center text-sm font-medium text-muted-foreground py-2">
              {day}
            </div>
          ))}
        </div>

        {/* Calendar Days */}
        <div className="grid grid-cols-7 gap-2">
          {/* Empty cells for days before month starts */}
          {Array?.from({ length: startingDayOfWeek })?.map((_, index) => (
            <div key={`empty-${index}`} className="aspect-square" />
          ))}

          {/* Days of the month */}
          {Array?.from({ length: daysInMonth })?.map((_, index) => {
            const day = index + 1;
            const dayTasks = getTasksForDate(day);
            const hasUrgent = dayTasks?.some(t => t?.priority === 'urgent');
            const hasHigh = dayTasks?.some(t => t?.priority === 'high');
            const isSelected = selectedDate === day;
            const isTodayDate = isToday(day);
            const isOverdueDate = isOverdue(day) && dayTasks?.some(t => t?.status !== 'completed');

            return (
              <button
                key={day}
                onClick={() => handleDateClick(day)}
                className={`
                  aspect-square p-2 rounded-lg border transition-all duration-250 relative
                  ${isSelected ? 'border-primary bg-primary/10' : 'border-border hover:border-primary/50'}
                  ${isTodayDate ? 'bg-primary/5' : ''}
                  ${isOverdueDate ? 'bg-error/5' : ''}
                `}
              >
                <div className="flex flex-col items-center justify-center h-full">
                  <span
                    className={`
                      text-sm font-medium
                      ${isTodayDate ? 'text-primary font-semibold' : 'text-foreground'}
                      ${isOverdueDate ? 'text-error' : ''}
                    `}
                  >
                    {day}
                  </span>
                  {dayTasks?.length > 0 && (
                    <div className="flex gap-0.5 mt-1">
                      {hasUrgent && <div className="w-1.5 h-1.5 rounded-full bg-error" />}
                      {hasHigh && <div className="w-1.5 h-1.5 rounded-full bg-warning" />}
                      {!hasUrgent && !hasHigh && <div className="w-1.5 h-1.5 rounded-full bg-primary" />}
                    </div>
                  )}
                </div>
              </button>
            );
          })}
        </div>

        {/* Legend */}
        <div className="flex items-center gap-4 mt-6 pt-4 border-t border-border">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-error" />
            <span className="text-xs text-muted-foreground">Urgent</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-warning" />
            <span className="text-xs text-muted-foreground">High Priority</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-primary" />
            <span className="text-xs text-muted-foreground">Normal/Low</span>
          </div>
        </div>
      </div>

      {/* Selected Date Tasks */}
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-foreground">
            {selectedDate
              ? `${monthNames?.[month]} ${selectedDate}`
              : 'Select a date'}
          </h3>
          {selectedDate && (
            <Button
              size="sm"
              onClick={onCreateTask}
              className="flex items-center gap-1"
            >
              <Icon name="Plus" size={16} />
            </Button>
          )}
        </div>

        {!selectedDate ? (
          <div className="text-center py-12">
            <Icon name="Calendar" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-3" />
            <p className="text-sm text-muted-foreground">
              Click on a date to view tasks
            </p>
          </div>
        ) : selectedDateTasks?.length === 0 ? (
          <div className="text-center py-12">
            <Icon name="CheckSquare" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-3" />
            <p className="text-sm text-muted-foreground mb-3">
              No tasks for this date
            </p>
            <Button size="sm" onClick={onCreateTask}>
              Add Task
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            {selectedDateTasks?.map((task) => (
              <div
                key={task?.id}
                className="p-3 rounded-lg border border-border hover:border-primary/50 transition-colors"
              >
                <div className="flex items-start gap-2">
                  <button
                    onClick={() => onUpdateTask(task?.id, {
                      status: task?.status === 'completed' ? 'todo' : 'completed'
                    })}
                  >
                    <Icon
                      name={task?.status === 'completed' ? 'CheckCircle2' : 'Circle'}
                      size={18}
                      color={task?.status === 'completed' ? 'var(--color-success)' : 'var(--color-muted-foreground)'}
                    />
                  </button>
                  <div className="flex-1">
                    <p className={`text-sm font-medium text-foreground ${task?.status === 'completed' ? 'line-through opacity-60' : ''}`}>
                      {task?.title}
                    </p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className={`w-2 h-2 rounded-full ${getPriorityColor(task?.priority)}`} />
                      <span className="text-xs text-muted-foreground capitalize">{task?.priority}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default DeadlineCalendar;